/**
 * WorkspaceOnboardingWizard
 *
 * Three modes:
 * - "onboarding"    (new users):     site → setup → success   (workspace auto-created)
 * - "addSite"       (existing users): site → workspace → setup → success
 * - "addWorkspace"  (existing users): workspace → site(select existing | add new) → [setup] → success
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { useTheme, type ThemePreference } from "@/lib/contexts/ThemeContext";
import { toast } from "sonner";

// API
import {
  getAllWorkspaces,
  createWorkspace,
  type WorkspaceWithGrouping,
} from "@/lib/api/workspaces";
import {
  addCustomSiteToWorkspace,
  createProperty,
  getAllProperties,
  getWorkspaceOwnerScriptId,
  getUserFacingApiError,
} from "@/lib/api/api";
import {
  getAvailableProperties,
  movePropertyToWorkspace,
} from "@/lib/api/workspace-team";
import type { AvailableProperty } from "@/lib/types/workspace-team";
import { generateSlugId } from "@/lib/routing/slug-id";

// UI Components
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { showIntercom } from "@/lib/utils/intercom";
import { track } from "@/lib/utils/track";
import { InlineTip } from "@/components/onboarding/InlineTip";
import { InstallScriptPanel } from "@/components/onboarding/InstallScriptPanel";
import type { PlatformId } from "@/lib/types/platform";
import { setSiteContext } from "@/lib/utils/siteContext";
import { pickScriptId, scriptIdFromOwnerResponse } from "@/lib/utils/trackingSnippet";

// Utilities
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Alert01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Comment02Icon,
  EarthIcon,
  FileAttachmentIcon,
  InformationCircleIcon,
  Loading01Icon,
  PlusSignIcon,
  Tick01Icon,
} from "@hugeicons/core-free-icons"

type WizardMode = "onboarding" | "addSite" | "addWorkspace";
type WizardStep = "workspace" | "site" | "setup" | "success";

/** Sentinel values for the inline workspace select on the site step. */
const WS_NEW = "__new-workspace__";
const WS_NONE = "__no-workspace__";

/** Steps shown per wizard mode */
function getStepsForMode(mode: WizardMode, isAddingNewSite?: boolean): WizardStep[] {
  switch (mode) {
    case "onboarding":
      return ["site", "setup", "success"];
    case "addSite":
      // Workspace assignment is an inline control on the site step, not a screen.
      return ["site", "setup", "success"];
    case "addWorkspace":
      return isAddingNewSite
        ? ["workspace", "site", "setup", "success"]
        : ["workspace", "site", "success"];
  }
}

interface WorkspaceOnboardingWizardProps {
  mode?: WizardMode;
  preselectedWorkspaceId?: string;
}

/** Helper: returns step title + description for use above the card */
function getStepMeta(
  step: WizardStep,
  mode: WizardMode,
  isCreatingNewWorkspace: boolean,
  isAddingNewSite?: boolean,
  /** False when the user skipped verification. The success header must not
   *  claim completion the install never earned. */
  wasVerified = true,
): { subtitle: string; title: string; description: string } {
  const steps = getStepsForMode(mode, isAddingNewSite);
  const idx = steps.indexOf(step) + 1;
  const subtitle = `Step ${idx} of ${steps.length}`;

  switch (step) {
    case "workspace":
      if (mode === "addWorkspace") {
        return {
          subtitle,
          title: "Create workspace",
          description: "Create a workspace to organize your sites and team",
        };
      }
      return {
        subtitle,
        title: isCreatingNewWorkspace ? "Create New Workspace" : "Select Workspace",
        description: isCreatingNewWorkspace
          ? "Create a workspace to organize your sites"
          : "Choose which workspace to add the site to",
      };
    case "site":
      if (mode === "addWorkspace") {
        return {
          subtitle,
          title: isAddingNewSite ? "Add Your Website" : "Assign Sites",
          description: isAddingNewSite
            ? "Enter your website domain to start tracking"
            : "Optionally assign existing sites to this workspace",
        };
      }
      return {
        subtitle,
        title: "Add Your Website",
        description: "Enter your website domain to start tracking",
      };
    case "setup":
      return {
        subtitle,
        title: "Connect your tracking",
        description:
          "Paste one snippet into your site, publish, then verify. About 2 minutes.",
      };
    case "success":
      if (mode !== "addWorkspace" && !wasVerified) {
        return {
          subtitle,
          title: "Site added",
          description: "Tracking is not live yet. Paste the snippet and publish to finish.",
        };
      }
      return {
        subtitle,
        title: "You're All Set!",
        description: mode === "onboarding"
          ? "Your site is ready. Head to your dashboard to explore."
          : "Your workspace has been set up successfully",
      };
  }
}

export default function WorkspaceOnboardingWizard({
  mode: propMode,
  preselectedWorkspaceId,
}: WorkspaceOnboardingWizardProps) {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { isReadOnly, readOnlyReason, openPricingModal } = useSubscription();
  const { preference, setTheme } = useTheme();
  const previousTheme = useRef<string | null>(null);
  const [searchParams] = useSearchParams();

  // Determine mode from props or URL
  const urlMode = searchParams.get("mode") as WizardMode | null;
  const mode: WizardMode = propMode || urlMode || "addSite";
  // Workspace settings (and similar entry points) pass ?workspace=<id> so the
  // new site lands in the workspace the user was just looking at.
  const scopedWorkspaceId =
    preselectedWorkspaceId || searchParams.get("workspace") || null;

  // Wizard state — start at the first step for this mode
  const [step, setStep] = useState<WizardStep>(() => getStepsForMode(mode)[0]);
  const [isLoading, setIsLoading] = useState(true);

  // Data state
  const [workspaces, setWorkspaces] = useState<WorkspaceWithGrouping[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    scopedWorkspaceId
  );
  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceDescription, setWorkspaceDescription] = useState("");

  // Workspace creation mode toggle — "addWorkspace" always creates new
  const [isCreatingNewWorkspace, setIsCreatingNewWorkspace] = useState(mode === "addWorkspace");

  // Site state
  const [isAddingNewSite, setIsAddingNewSite] = useState(true);

  // Derived step list — memoized so the useEffect dep below doesn't fire every render
  const modeSteps = useMemo(
    () => getStepsForMode(mode, isAddingNewSite),
    [mode, isAddingNewSite],
  );

  const [availableSites, setAvailableSites] = useState<AvailableProperty[]>([]);
  const [selectedExistingSiteId, setSelectedExistingSiteId] = useState<string | null>(null);
  const [siteSearchQuery, setSiteSearchQuery] = useState("");
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState("");
  const [domain, setDomain] = useState("");
  const [siteName, setSiteName] = useState("");
  const [domainError, setDomainError] = useState("");
  const [hasUserEditedSiteName, setHasUserEditedSiteName] = useState(false);
  const [isEditingSiteName, setIsEditingSiteName] = useState(false);

  // Script/verification state — verify UX lives inside InstallScriptPanel.
  // `wasVerified` is the difference between "we saw your script" and "you told
  // us to move on". The success step must not claim the former when only the
  // latter happened.
  const [scriptId, setScriptId] = useState("");
  const [scriptIdLoadFailed, setScriptIdLoadFailed] = useState(false);
  const [wasVerified, setWasVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Which workspace a fetched (non-JWT) scriptId belongs to. A JWT scriptId is
  // the owner's own loader id and is workspace-independent (null here); a
  // workspace-owner fetch is pinned to its workspace so switching workspaces
  // re-resolves instead of pasting the previous owner's tracking id.
  const scriptIdSourceWorkspaceRef = useRef<string | null>(null);

  // Guards against double-clicks racing workspace creation / site submission
  const workspaceCreationRef = useRef<Promise<string | null> | null>(null);
  const finalizationInFlightRef = useRef(false);

  // Success state
  const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);
  const [detectedPlatform, setDetectedPlatform] = useState<PlatformId | null>(null);
  const [skippedWorkspace, setSkippedWorkspace] = useState(false);

  // Safety: if modeSteps changes and current step is no longer valid, reset to "site"
  useEffect(() => {
    if (!modeSteps.includes(step)) {
      setStep("site");
    }
  }, [modeSteps, step]);

  // Force light mode on wizard, restore previous theme on unmount
  useEffect(() => {
    previousTheme.current = preference;
    if (preference !== 'light') {
      setTheme('light');
    }
    return () => {
      if (previousTheme.current && previousTheme.current !== 'light') {
        setTheme(previousTheme.current as ThemePreference);
      }
    };
  }, []);

  // Load workspaces + mode-specific setup
  useEffect(() => {
    const loadData = async () => {
      if (!token) {
        navigate("/auth");
        return;
      }

      setIsLoading(true);
      try {
        const [ws, allPropsResponse, propertiesResult] = await Promise.all([
          getAllWorkspaces(token),
          getAllProperties(token).catch(() => ({ properties: [] })),
          getAvailableProperties(token),
        ]);
        setWorkspaces(ws || []);

        // Build available sites: try dedicated endpoint first, then
        // getAllProperties (property/all), then extract from workspaces
        const apiSites = propertiesResult?.properties || [];
        if (apiSites.length > 0) {
          setAvailableSites(apiSites);
        } else {
          // Build from getAllProperties response + workspace assignment info
          const allProps: any[] = allPropsResponse?.properties || [];

          if (allProps.length > 0) {
            // Build a map of property → workspace from workspace data
            const propWorkspaceMap = new Map<string, { workspaceId: string; workspaceName: string }>();
            (ws || []).forEach((w) => {
              (w.properties || []).forEach((p) => {
                propWorkspaceMap.set(p.propertyId, {
                  workspaceId: w.workspaceId,
                  workspaceName: w.name,
                });
              });
            });

            const sitesFromProps: AvailableProperty[] = allProps
              .filter((p: any) => !p.archived)
              .map((p: any) => {
                const domain = p.domain ||
                  (p.dataSources?.[0]?.config as any)?.domain ||
                  p.name || "";
                const wsInfo = propWorkspaceMap.get(p.propertyId);
                return {
                  property_id: p.propertyId,
                  name: p.name || domain,
                  domain,
                  current_workspace_id: wsInfo?.workspaceId || null,
                  current_workspace_name: wsInfo?.workspaceName,
                  enabled: true,
                  tracking_status: (p.tracking_status || 'not_installed') as any,
                };
              });
            setAvailableSites(sitesFromProps);
          } else if (ws && ws.length > 0) {
            // Final fallback: extract from workspace properties
            const sitesFromWorkspaces: AvailableProperty[] = ws.flatMap((w) =>
              (w.properties || []).map((p) => ({
                property_id: p.propertyId,
                name: p.name,
                domain: p.domain,
                current_workspace_id: w.workspaceId,
                current_workspace_name: w.name,
                enabled: p.enabled,
                tracking_status: p.trackingStatus,
              }))
            );
            setAvailableSites(sitesFromWorkspaces);
          }
        }

        // Set script ID from the JWT when the claim exists. Team members
        // without it hydrate from the workspace owner on the setup step.
        const fromJwt = pickScriptId(user?.scriptId);
        if (fromJwt) {
          setScriptId(fromJwt);
        }

        if (mode === "onboarding") {
          // Auto-create a default workspace for new users (or use their first if one exists)
          if (ws && ws.length > 0) {
            setSelectedWorkspaceId(ws[0].workspaceId);
          } else {
            // Create a default workspace silently
            const defaultName = user?.name ? `${user.name}'s Workspace` : "My Workspace";
            try {
              const newWs = await createWorkspace(defaultName, undefined, token);
              setWorkspaces([newWs]);
              setSelectedWorkspaceId(newWs.workspaceId);
            } catch {
              toast.error("Failed to set up workspace. Please refresh.");
            }
          }
          setIsAddingNewSite(true);
        } else if (mode === "addSite") {
          // Default to the first existing workspace; if none exist, silently
          // create a default one at finalization (no workspace screen).
          // A ?workspace= (or preselectedWorkspaceId) pin wins when it matches.
          if (ws && ws.length > 0) {
            setIsCreatingNewWorkspace(false);
            const scopedMatch = scopedWorkspaceId
              ? ws.find((w) => w.workspaceId === scopedWorkspaceId)
              : undefined;
            setSelectedWorkspaceId(scopedMatch?.workspaceId ?? ws[0].workspaceId);
          } else {
            setIsCreatingNewWorkspace(true);
            setWorkspaceName(user?.name ? `${user.name}'s Workspace` : "My Workspace");
          }
          // addSite always adds a new site
          setIsAddingNewSite(true);
        } else if (mode === "addWorkspace") {
          // Always creating a new workspace
          setIsCreatingNewWorkspace(true);
          // Only allow selecting existing sites
          setIsAddingNewSite(false);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
        toast.error("Failed to load data. Please refresh the page.");
        setWorkspaces([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [token, mode, navigate, user, scopedWorkspaceId]);

  useEffect(() => {
    if (step !== "setup" || !token) return;

    const fromJwt = pickScriptId(user?.scriptId);
    if (fromJwt) {
      if (scriptId !== fromJwt) setScriptId(fromJwt);
      scriptIdSourceWorkspaceRef.current = null;
      setScriptIdLoadFailed(false);
      return;
    }

    if (isLoading) return;

    if (!selectedWorkspaceId) {
      // "+ New workspace" / "Don't assign" cleared the selection. Drop any
      // previously resolved owner id so a stale/wrong snippet isn't shown while
      // there's no workspace to resolve against.
      if (scriptId) {
        scriptIdSourceWorkspaceRef.current = null;
        setScriptId("");
      }
      setScriptIdLoadFailed(true);
      return;
    }

    // Already resolved for exactly this workspace — keep it.
    if (scriptId && scriptIdSourceWorkspaceRef.current === selectedWorkspaceId) return;

    // Team member switched workspaces after a prior resolve — drop the previous
    // owner's id so the panel shows loading (not a stale/wrong id) and let this
    // effect re-run against the newly selected workspace.
    if (scriptId) {
      scriptIdSourceWorkspaceRef.current = null;
      setScriptId("");
      return;
    }

    let cancelled = false;
    setScriptIdLoadFailed(false);
    getWorkspaceOwnerScriptId(selectedWorkspaceId, token)
      .then((payload) => {
        if (cancelled) return;
        const id = scriptIdFromOwnerResponse(payload);
        if (id) {
          setScriptId(id);
          scriptIdSourceWorkspaceRef.current = selectedWorkspaceId;
          setScriptIdLoadFailed(false);
        } else {
          setScriptIdLoadFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setScriptIdLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [step, scriptId, token, user?.scriptId, selectedWorkspaceId, isLoading]);

  // Handle workspace step "Next" — defer creation until finalization
  const handleCreateWorkspace = async () => {
    if (!token || !workspaceName.trim()) return;

    // Don't create yet — just advance to the next step.
    // The workspace will be created when the user finalizes (attaches a site, adds a new site, or skips).
    goToNextStep();
  };

  // Actually create the workspace via API (called at finalization).
  // Dedupes concurrent callers via a ref so rapid double-clicks never create twice.
  const ensureWorkspaceCreated = async (): Promise<string | null> => {
    // If we already have a selected workspace (existing or previously created), use it
    if (selectedWorkspaceId) return selectedWorkspaceId;

    if (!token || !workspaceName.trim()) return null;

    if (workspaceCreationRef.current) return workspaceCreationRef.current;

    const promise = (async () => {
      try {
        const newWs = await createWorkspace(
          workspaceName.trim(),
          workspaceDescription.trim() || undefined,
          token
        );
        setWorkspaces(prev => [...prev, newWs]);
        setSelectedWorkspaceId(newWs.workspaceId);
        return newWs.workspaceId;
      } catch (error) {
        console.error("Failed to create workspace:", error);
        toast.error("Failed to create workspace");
        workspaceCreationRef.current = null; // allow retry
        return null;
      }
    })();

    workspaceCreationRef.current = promise;
    return promise;
  };

  // Handle continuing with selected workspace
  const handleSelectWorkspaceContinue = () => {
    if (!selectedWorkspaceId) return;
    setSkippedWorkspace(false);
    goToNextStep();
  };

  // Handle attaching an existing site to the workspace
  const handleAttachExistingSite = async () => {
    if (!selectedExistingSiteId || !token) return;

    setIsSubmitting(true);
    try {
      // Ensure workspace exists (creates if deferred)
      const wsId = await ensureWorkspaceCreated();
      if (!wsId) {
        toast.error("Failed to create workspace");
        setIsSubmitting(false);
        return;
      }

      await movePropertyToWorkspace(wsId, selectedExistingSiteId, token);
      setCreatedPropertyId(selectedExistingSiteId);
      toast.success("Site attached to workspace!");
      setStep("success");
    } catch (error) {
      console.error("Failed to attach site:", error);
      toast.error("Failed to attach site to workspace");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Domain validation with visual feedback
  const validateDomain = useCallback((input: string): boolean => {
    if (!input.trim()) {
      setDomainError("");
      return false;
    }

    let cleanDomain = input;
    try {
      const url = new URL(input);
      cleanDomain = url.hostname;
    } catch {
      cleanDomain = input
        .replace(/^(https?:\/\/)?(www\.)?/i, "")
        .split("/")[0]
        .split("?")[0];
    }

    const domainRegex =
      /^(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})$/;

    const domainWithoutWWW = cleanDomain.replace(/^www\./i, "");
    if (domainRegex.test(domainWithoutWWW)) {
      setDomain(domainWithoutWWW);
      setDomainError("");
      return true;
    }

    setDomainError(
      "Invalid format. Enter domain without 'www' or 'http://' (e.g., example.com)"
    );
    return false;
  }, []);

  // Auto-generate display name from domain
  const deriveSiteName = useCallback((domainValue: string): string => {
    if (!domainValue) return "";
    const base = domainValue.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
    const words = base.split(" ").filter(Boolean);
    if (!words.length) return domainValue;
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  // Handle domain input change
  const handleDomainChange = (value: string) => {
    setDomain(value);
    setDomainError("");

    // Auto-generate site name if user hasn't manually edited it
    if (!hasUserEditedSiteName) {
      const cleanValue = value.replace(/^(https?:\/\/)?(www\.)?/i, "").split("/")[0];
      setSiteName(deriveSiteName(cleanValue));
    }
  };

  // Handle site name change (mark as user-edited)
  const handleSiteNameChange = (value: string) => {
    setSiteName(value);
    setHasUserEditedSiteName(true);
  };

  // Inline workspace select on the site step (addSite mode)
  const handleInlineWorkspaceChange = (value: string) => {
    if (value === WS_NEW) {
      setIsCreatingNewWorkspace(true);
      setSelectedWorkspaceId(null);
      setSkippedWorkspace(false);
    } else if (value === WS_NONE) {
      setSkippedWorkspace(true);
      setSelectedWorkspaceId(null);
      setIsCreatingNewWorkspace(false);
    } else {
      setSelectedWorkspaceId(value);
      setSkippedWorkspace(false);
      setIsCreatingNewWorkspace(false);
    }
  };

  // Check if domain is valid (for UI feedback)
  const isDomainValid = domain.trim() !== "" && !domainError;

  // Shared finalization: create the site (+ workspace if deferred), advance to success.
  // Guarded by finalizationInFlightRef so double-clicks can never double-submit.
  const finalizeSite = async (skipVerification: boolean): Promise<boolean> => {
    if (finalizationInFlightRef.current) return false;
    // The route is reachable directly, so the disabled Add site buttons on the
    // hub and Workspace settings aren't a sufficient gate on their own.
    if (isReadOnly) {
      toast.error(
        readOnlyReason === "trial_ended"
          ? "Your trial has ended. Reactivate to add a site."
          : "Your subscription has ended. Upgrade to add a site."
      );
      openPricingModal();
      return false;
    }
    finalizationInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      let siteResult;
      if (skippedWorkspace) {
        siteResult = await createProperty(siteName.trim(), domain, token!);
      } else {
        const wsId = await ensureWorkspaceCreated();
        if (!wsId) return false;

        siteResult = await addCustomSiteToWorkspace(
          wsId,
          domain,
          siteName.trim(),
          false,
          token!,
          true, // Enable onboarding
          skipVerification,
        );
      }

      const propertyId = siteResult?.propertyId || siteResult?.property?.propertyId;
      if (!propertyId) {
        if (siteResult?.duplicate) throw new Error("Site already exists in this workspace");
        throw new Error("No property ID returned");
      }

      setCreatedPropertyId(propertyId);
      // Soft-cache platform detected during install for CreateExperiment tips.
      if (detectedPlatform) {
        setSiteContext(propertyId, { platform: detectedPlatform, domain });
      }
      toast.success(siteResult?.duplicate ? "Site already connected!" : "Site added successfully!");
      setStep("success");
      return true;
    } catch (error) {
      console.error("Failed to add site:", error);
      toast.error(getUserFacingApiError(error, "Failed to add site"));
      return false;
    } finally {
      setIsSubmitting(false);
      finalizationInFlightRef.current = false;
    }
  };

  // Skip verification and create the site anyway
  const handleSkipVerification = async () => {
    if (!domain || !siteName.trim()) return;
    await finalizeSite(true);
  };

  // Navigate after completion — land on the new site's analytics page.
  // Exception: if the user created a workspace without attaching any site
  // (addWorkspace mode, "Don't assign a site"), send them to /connectors
  // since /traffic would be empty.
  const handleFinishSetup = () => {
    if (skippedWorkspace || !selectedWorkspaceId) {
      navigate("/dashboard");
      return;
    }

    const ws = workspaces.find(w => w.workspaceId === selectedWorkspaceId);
    const slugId = generateSlugId(ws?.name || workspaceName, selectedWorkspaceId);

    if (!createdPropertyId) {
      navigate(`/workspace/${slugId}/connectors`);
      return;
    }

    navigate(`/workspace/${slugId}/traffic?site=${createdPropertyId}`);
  };

  // Navigation helpers based on mode-specific step list
  const goToNextStep = () => {
    const idx = modeSteps.indexOf(step);
    if (idx < modeSteps.length - 1) setStep(modeSteps[idx + 1]);
  };
  const goToPrevStep = () => {
    const idx = modeSteps.indexOf(step);
    if (idx > 0) setStep(modeSteps[idx - 1]);
  };

  const stepMeta = getStepMeta(step, mode, isCreatingNewWorkspace, isAddingNewSite, wasVerified);

  if (isLoading) {
    return (
      <div className="min-h-svh bg-background flex items-center justify-center">
        <HugeiconsIcon icon={Loading01Icon} size={32} className="animate-spin text-foreground/50" />
      </div>
    );
  }

  return (
    <div className="h-svh max-w-[100vw] bg-background flex justify-center overflow-x-hidden">
      {/* The install step carries a snippet, a platform picker and a preview,
          so it gets a wider column to stay inside one laptop viewport. The
          other steps are short forms and stay narrow. */}
      <div className={cn(
        "relative flex w-full flex-col bg-background min-w-0 h-full overflow-x-hidden overflow-y-auto",
        step === "setup" ? "max-w-4xl" : "max-w-2xl",
      )}>
        {/* Subtle atmosphere — kept light so MacBook fold stays content-first */}
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.35]"
          aria-hidden="true"
          style={{
            backgroundImage: 'radial-gradient(circle, var(--color-border) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 z-0"
          aria-hidden="true"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, var(--color-background) 78%)',
          }}
        />

        <div className="relative z-[2] flex flex-1 flex-col w-full min-w-0 px-5 py-4 sm:px-8 sm:py-5 mx-auto">
          {/* Back only. Both routes that mount this wizard (/add-website,
              /workspace/new) are reached from inside the app, so a logo lockup
              and a numbered stepper are chrome the user does not need and the
              install step cannot spare the vertical space for. */}
          <div className="flex items-center justify-end shrink-0">
            {mode !== "onboarding" && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
                onClick={() => navigate(-1)}
              >
                <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
                Back
              </Button>
            )}
          </div>

          {/* Step header. With the top stepper removed this eyebrow is the only
              progress signal, so it renders on every step. */}
          <div className={cn("shrink-0 space-y-0.5 text-center sm:text-left", step === "setup" ? "mt-3" : "mt-5")}>
            <p className="text-xs font-medium text-primary/80">
              {stepMeta.subtitle}
            </p>
            <h1 className={cn(
              "font-medium tracking-tight text-foreground",
              step === "setup" ? "text-lg" : "text-xl",
            )}>
              {stepMeta.title}
            </h1>
            <p className="text-xs text-muted-foreground max-w-md mx-auto sm:mx-0">
              {stepMeta.description}
            </p>
          </div>

          {/* Main card — denser on install so Copy + verify clear the laptop fold */}
          <Card className={cn(
            "border-border/30 min-w-0",
            // Card ships overflow-hidden, which makes it the containing
            // block for sticky descendants and pins the verify CTA to the
            // card bottom instead of the viewport. Release it on install.
            step === "setup" ? "mt-3 overflow-visible" : "mt-5",
          )}>
            <CardContent className={cn(step === "setup" ? "p-4" : "p-6")}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Step 1: Workspace (addWorkspace mode only) */}
                  {step === "workspace" && (
                    <div className="space-y-4">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={isCreatingNewWorkspace ? "create" : "select"}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-4"
                        >
                          {isCreatingNewWorkspace ? (
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor="workspace-name">
                                    Workspace Name <span className="text-destructive">*</span>
                                  </Label>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        Workspaces group your sites and team members. You can share access with teammates.
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                                <Input
                                  id="workspace-name"
                                  value={workspaceName}
                                  onChange={(e) => setWorkspaceName(e.target.value)}
                                  placeholder="e.g., My Company, Client Projects"
                                />
                                <p className="text-xs text-muted-foreground">
                                  Group related sites together for easy management
                                </p>
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="workspace-description">Description (optional)</Label>
                                <Textarea
                                  id="workspace-description"
                                  value={workspaceDescription}
                                  onChange={(e) => setWorkspaceDescription(e.target.value)}
                                  placeholder="Brief description of this workspace"
                                  rows={2}
                                  className="text-sm"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Label htmlFor="workspace-search">Workspace</Label>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      Choose which workspace this site will belong to. Team members in the workspace will have access.
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                              <div className="space-y-1.5">
                                <Input
                                  id="workspace-search"
                                  placeholder="Search workspaces..."
                                  value={workspaceSearchQuery}
                                  onChange={(e) => setWorkspaceSearchQuery(e.target.value)}
                                />
                                <div className="divide-y divide-border/40 rounded-lg border border-border/30 overflow-hidden max-h-[220px] overflow-y-auto">
                                  {workspaces
                                    .filter(
                                      (ws) =>
                                        !workspaceSearchQuery ||
                                        ws.name.toLowerCase().includes(workspaceSearchQuery.toLowerCase())
                                    )
                                    .map((ws) => {
                                      const isSelected = selectedWorkspaceId === ws.workspaceId;
                                      const siteCount = ws.properties?.length ?? 0;
                                      return (
                                        <button
                                          key={ws.workspaceId}
                                          type="button"
                                          onClick={() => setSelectedWorkspaceId(ws.workspaceId)}
                                          className={cn(
                                            "flex items-center gap-3 w-full px-3 py-2.5 text-left transition-colors",
                                            isSelected
                                              ? "bg-muted/60"
                                              : "bg-card hover:bg-muted/30"
                                          )}
                                        >
                                          <div className="h-7 w-7 rounded-md bg-muted flex items-center justify-center text-[11px] font-semibold text-muted-foreground shrink-0">
                                            {ws.name.charAt(0).toUpperCase()}
                                          </div>
                                          <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate">{ws.name}</p>
                                            <p className="text-[11px] text-muted-foreground">
                                              {siteCount} site{siteCount !== 1 ? "s" : ""}
                                            </p>
                                          </div>
                                          {isSelected && (
                                            <HugeiconsIcon icon={Tick01Icon} size={16} className="text-foreground shrink-0" />
                                          )}
                                        </button>
                                      );
                                    })}
                                </div>
                                {workspaces.filter(
                                  (ws) =>
                                    !workspaceSearchQuery ||
                                    ws.name.toLowerCase().includes(workspaceSearchQuery.toLowerCase())
                                ).length === 0 && (
                                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                                    No workspaces found.{" "}
                                    <button
                                      type="button"
                                      onClick={() => setIsCreatingNewWorkspace(true)}
                                      className="text-primary hover:underline"
                                    >
                                      Create your first workspace
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      </AnimatePresence>

                      <InlineTip variant="card">
                        <span className="font-medium text-foreground">Tip:</span> Workspaces help you organize sites by client, project, or team. You can create unlimited workspaces.
                      </InlineTip>

                      {/* Workspace step footer */}
                      <div className="flex gap-3 pt-5 mt-5 border-t border-border/30">
                        {/* Back button — only if there's a previous step */}
                        {modeSteps.indexOf("workspace") > 0 && (
                          <Button
                            variant="outline"
                            size="lg"
                            className="gap-2"
                            onClick={goToPrevStep}
                          >
                            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
                            Back
                          </Button>
                        )}
                        <Button
                          size="lg"
                          className="flex-1 gap-2"
                          onClick={isCreatingNewWorkspace ? handleCreateWorkspace : handleSelectWorkspaceContinue}
                          disabled={isCreatingNewWorkspace ? !workspaceName.trim() : !selectedWorkspaceId}
                        >
                          Next
                          <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Site Details */}
                  {step === "site" && (
                    <div className="space-y-4">
                      {/* Tab toggle — only for addWorkspace mode */}
                      {mode === "addWorkspace" && (
                        <Tabs
                          value={isAddingNewSite ? "add" : "select"}
                          onValueChange={(v) => {
                            const adding = v === "add";
                            setIsAddingNewSite(adding);
                            // Reset state when switching tabs
                            setSelectedExistingSiteId(null);
                            setSiteSearchQuery("");
                            setDomain("");
                            setSiteName("");
                            setDomainError("");
                            setHasUserEditedSiteName(false);
                          }}
                        >
                          <TabsList className="inline-flex items-center gap-0.5 border border-border/30 rounded-md bg-muted/30 p-[3px] h-9">
                            <TabsTrigger
                              value="select"
                              className="gap-2 px-3 h-full text-[13px] font-medium rounded-md transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-border/30 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/80 data-[state=inactive]:hover:bg-muted/40"
                            >
                              Select Existing
                            </TabsTrigger>
                            <TabsTrigger
                              value="add"
                              className="gap-1.5 px-3 h-full text-[13px] font-medium rounded-md transition-all duration-200 data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-border/30 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground/80 data-[state=inactive]:hover:bg-muted/40"
                            >
                              <HugeiconsIcon icon={PlusSignIcon} size={14} />
                              Add New
                            </TabsTrigger>
                          </TabsList>
                        </Tabs>
                      )}

                      <AnimatePresence mode="wait">
                        <motion.div
                          key={isAddingNewSite ? "add" : "select"}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-4"
                        >
                      {isAddingNewSite ? (
                        <div className="space-y-4">
                          {/* Domain Field — prefix-adorned, single hero input */}
                          <div className="space-y-1.5">
                            <Label htmlFor="domain" className="text-xs">
                              Domain <span className="text-destructive">*</span>
                            </Label>
                            <div
                              className={cn(
                                "flex items-stretch overflow-hidden rounded-md border bg-input/20 dark:bg-input/30 transition-colors focus-within:border-ring focus-within:ring-[2px] focus-within:ring-ring/30",
                                domainError
                                  ? "border-destructive ring-[2px] ring-destructive/20"
                                  : "border-input",
                              )}
                            >
                              <span className="flex select-none items-center border-r border-input bg-muted/40 px-2.5 text-xs text-muted-foreground">
                                https://
                              </span>
                              <input
                                id="domain"
                                value={domain}
                                onChange={(e) => handleDomainChange(e.target.value)}
                                onBlur={() => validateDomain(domain)}
                                placeholder="example.com"
                                autoFocus
                                autoCapitalize="none"
                                autoCorrect="off"
                                autoComplete="off"
                                className="h-9 w-full min-w-0 bg-transparent px-2.5 text-sm outline-none placeholder:text-muted-foreground"
                              />
                              {domain && !domainError && (
                                <span className="flex items-center pr-2.5 text-success dark:text-success">
                                  <HugeiconsIcon icon={Tick01Icon} size={14} />
                                </span>
                              )}
                              {domainError && (
                                <span className="flex items-center pr-2.5 text-destructive">
                                  <HugeiconsIcon icon={Alert01Icon} size={14} />
                                </span>
                              )}
                            </div>
                            {domainError ? (
                              <p className="text-[11px] text-destructive">{domainError}</p>
                            ) : (
                              <p className="text-[11px] text-muted-foreground/70">
                                Just the domain. No www or paths needed.
                              </p>
                            )}
                          </div>

                          {/* Display name — auto-derived, edit on demand */}
                          {isEditingSiteName ? (
                            <div className="space-y-1.5">
                              <Label htmlFor="site-name" className="text-xs">
                                Display name
                              </Label>
                              <Input
                                id="site-name"
                                value={siteName}
                                onChange={(e) => handleSiteNameChange(e.target.value)}
                                placeholder="My Website"
                                autoFocus
                                className="h-9"
                              />
                              <p className="text-[11px] text-muted-foreground/70">
                                Shown in your dashboard sidebar.
                              </p>
                            </div>
                          ) : (
                            siteName && (
                              <p className="text-[11px] text-muted-foreground">
                                Name:{" "}
                                <span className="font-medium text-foreground">{siteName}</span>
                                {" · "}
                                <button
                                  type="button"
                                  onClick={() => setIsEditingSiteName(true)}
                                  className="text-muted-foreground underline underline-offset-2 hover:text-foreground"
                                >
                                  Edit
                                </button>
                              </p>
                            )
                          )}

                          {/* Workspace — inline compact select (addSite only) */}
                          {mode === "addSite" && workspaces.length > 0 && (
                            <div className="space-y-1.5 border-t border-border/30 pt-4">
                              <Label className="text-xs">Workspace</Label>
                              <Select
                                value={
                                  skippedWorkspace
                                    ? WS_NONE
                                    : isCreatingNewWorkspace
                                      ? WS_NEW
                                      : selectedWorkspaceId ?? ""
                                }
                                onValueChange={handleInlineWorkspaceChange}
                              >
                                <SelectTrigger className="h-9 w-full">
                                  <SelectValue placeholder="Choose a workspace" />
                                </SelectTrigger>
                                <SelectContent>
                                  {workspaces.map((ws) => (
                                    <SelectItem key={ws.workspaceId} value={ws.workspaceId}>
                                      {ws.name}
                                      <span className="text-muted-foreground">
                                        {" "}· {ws.properties?.length ?? 0} site{(ws.properties?.length ?? 0) !== 1 ? "s" : ""}
                                      </span>
                                    </SelectItem>
                                  ))}
                                  <SelectItem value={WS_NEW}>+ New workspace</SelectItem>
                                  <SelectItem value={WS_NONE}>Don't assign to a workspace</SelectItem>
                                </SelectContent>
                              </Select>
                              {isCreatingNewWorkspace && (
                                <Input
                                  value={workspaceName}
                                  onChange={(e) => setWorkspaceName(e.target.value)}
                                  placeholder="Workspace name (e.g., My Company)"
                                  autoFocus
                                  className="h-9"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Search existing sites */}
                          <div className="relative">
                            <Input
                              placeholder="Search sites by domain or name..."
                              value={siteSearchQuery}
                              onChange={(e) => setSiteSearchQuery(e.target.value)}
                            />
                          </div>

                          {/* Sites list */}
                          <div className="divide-y divide-border/40 rounded-lg border border-border/30 overflow-hidden max-h-[220px] overflow-y-auto">
                            {availableSites
                              .filter(site =>
                                !siteSearchQuery ||
                                site.domain.toLowerCase().includes(siteSearchQuery.toLowerCase()) ||
                                site.name?.toLowerCase().includes(siteSearchQuery.toLowerCase())
                              )
                              .map((site) => {
                                const isSelected = selectedExistingSiteId === site.property_id;
                                const isAssigned = !!site.current_workspace_id;
                                return (
                                  <button
                                    key={site.property_id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedExistingSiteId(site.property_id);
                                      setDomain(site.domain);
                                      setSiteName(site.name || site.domain);
                                    }}
                                    className={cn(
                                      "flex items-center justify-between w-full px-3 py-2.5 text-left transition-colors",
                                      isSelected
                                        ? "bg-muted/60"
                                        : "bg-card hover:bg-muted/30"
                                    )}
                                  >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                      <HugeiconsIcon icon={EarthIcon} size={16} className="text-muted-foreground shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">
                                          {site.domain}
                                        </p>
                                        {site.name && site.name !== site.domain && (
                                          <p className="text-[11px] text-muted-foreground truncate">
                                            {site.name}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {isAssigned && (
                                        <span className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                          {site.current_workspace_name || "Assigned"}
                                        </span>
                                      )}
                                      {isSelected && (
                                        <HugeiconsIcon icon={Tick01Icon} size={16} className="text-foreground" />
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            {availableSites.filter(site =>
                              !siteSearchQuery ||
                              site.domain.toLowerCase().includes(siteSearchQuery.toLowerCase()) ||
                              site.name?.toLowerCase().includes(siteSearchQuery.toLowerCase())
                            ).length === 0 && (
                              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                                No sites found.{" "}
                                <button
                                  type="button"
                                  onClick={() => setIsAddingNewSite(true)}
                                  className="text-primary hover:underline"
                                >
                                  Add a new site instead
                                </button>
                              </div>
                            )}
                          </div>

                          {selectedExistingSiteId && (
                            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-800/30">
                              <HugeiconsIcon icon={InformationCircleIcon} size={14} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                              <p className="text-[11px] text-blue-700 dark:text-blue-300 leading-relaxed">
                                This site will be moved to the selected workspace. If it's already in another workspace, it will be reassigned.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                        </motion.div>
                      </AnimatePresence>

                      <InlineTip variant="card">
                        <span className="font-medium text-foreground">Tip:</span>{" "}
                        {isAddingNewSite
                          ? "Next you'll paste a small snippet into your site. We'll detect your platform (Webflow, Framer, WordPress...) and walk you through it."
                          : "Each site belongs to one workspace at a time. Moving a site here will remove it from its current workspace."}
                      </InlineTip>

                      {/* Site step footer */}
                      <div className="flex gap-3 pt-5 mt-5 border-t border-border/30">
                        {/* Back button — only if there's a previous step */}
                        {modeSteps.indexOf("site") > 0 && (
                          <Button
                            variant="outline"
                            size="lg"
                            className="flex-1 gap-2"
                            onClick={goToPrevStep}
                          >
                            <HugeiconsIcon icon={ArrowLeft01Icon} size={16} />
                            Back
                          </Button>
                        )}
                        {isAddingNewSite ? (
                          <Button
                            size="lg"
                            className="flex-1 gap-2"
                            onClick={goToNextStep}
                            disabled={
                              !isDomainValid ||
                              !siteName.trim() ||
                              (mode === "addSite" && isCreatingNewWorkspace && !workspaceName.trim())
                            }
                          >
                            Next
                            <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                          </Button>
                        ) : (
                          <div className="flex flex-1 gap-2">
                            {/* addWorkspace: allow skipping if no sites to assign */}
                            {mode === "addWorkspace" && (
                              <Button
                                variant="outline"
                                size="lg"
                                className="flex-1 gap-2"
                                onClick={async () => {
                                  setIsSubmitting(true);
                                  const wsId = await ensureWorkspaceCreated();
                                  setIsSubmitting(false);
                                  if (wsId) {
                                    toast.success("Workspace created!");
                                    setStep("success");
                                  }
                                }}
                                disabled={isSubmitting}
                              >
                                Don't assign a site
                              </Button>
                            )}
                            <Button
                              size="lg"
                              className="flex-1 gap-2"
                              onClick={handleAttachExistingSite}
                              disabled={!selectedExistingSiteId || isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <HugeiconsIcon icon={Loading01Icon} size={16} className="animate-spin" />
                                  Attaching...
                                </>
                              ) : (
                                <>
                                  Attach & Continue
                                  <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Setup / Install Script — shared guided panel */}
                  {step === "setup" && (
                    <div className="space-y-4">
                      {scriptId && token ? (
                        <InstallScriptPanel
                          domain={domain}
                          scriptId={scriptId}
                          token={token}
                          allowContinueUnverified
                          onCopied={(platform) =>
                            track("add_site_script_copied", token, { platform, domain })
                          }
                          onInstructionsExpanded={(platform) =>
                            track("add_site_install_instructions_expanded", token, { platform })
                          }
                          onPlatformDetected={(platform, source) => {
                            setDetectedPlatform(platform)
                            track("add_site_platform_detected", token, { platform, source })
                          }}
                          onVerified={async () => {
                            const ok = await finalizeSite(false);
                            if (ok) {
                              setWasVerified(true);
                              track("tracking_script_verified", token, { domain });
                            }
                          }}
                          onVerificationFailed={(attempt) =>
                            track("add_site_verification_failed", token, { attempt_count: attempt })
                          }
                          onContinueUnverified={(failCount) => {
                            track("add_site_verification_skipped", token, { fail_count: failCount });
                            handleSkipVerification();
                          }}
                        />
                      ) : (
                        <div className="rounded-lg border border-border/30 bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
                          {scriptIdLoadFailed ? (
                            "Couldn't load tracking id. Refresh and try again."
                          ) : (
                            <span className="inline-flex items-center gap-1.5">
                              <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                              Loading your tracking script...
                            </span>
                          )}
                        </div>
                      )}

                      {/* Setup step footer */}
                      <div className="flex items-center justify-between pt-3 mt-1 border-t border-border/30">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-muted-foreground hover:text-foreground h-8"
                          onClick={goToPrevStep}
                          disabled={isSubmitting}
                        >
                          <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
                          Back
                        </Button>
                        {isSubmitting && (
                          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
                            Adding site...
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Final Step: Success */}
                  {step === "success" && (
                    <div className="space-y-5">
                      {/* Dashboard Mockup */}
                      <div className="rounded-xl overflow-hidden border border-border/60 bg-card shadow-lg">
                        {/* Browser chrome */}
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30 bg-muted/30">
                          <div className="flex gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-red-400/80" />
                            <div className="w-2 h-2 rounded-full bg-yellow-400/80" />
                            <div className="w-2 h-2 rounded-full bg-success/80" />
                          </div>
                          <div className="flex-1 flex items-center justify-center">
                            <div className="flex items-center gap-1.5 px-3 py-0.5 rounded-md bg-muted/50 border border-border/30">
                              <svg width="8" height="8" viewBox="0 0 16 16" fill="none" className="text-success">
                                <path d="M8 1a5 5 0 0 0-5 5v2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2V6a5 5 0 0 0-5-5z" fill="currentColor" opacity="0.2" />
                                <path d="M8 1a5 5 0 0 0-5 5v2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2V6a5 5 0 0 0-5-5z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              </svg>
                              <span className="text-[8px] text-muted-foreground">{domain || 'yoursite.com'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Mini dashboard content */}
                        <div className="p-3 space-y-3">
                          {/* Top nav bar mockup */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="bg-primary text-primary-foreground flex size-5 items-center justify-center rounded-md">
                                <span className="text-[8px] font-semibold">H</span>
                              </div>
                              <span className="text-[11px] font-semibold text-foreground">{siteName || 'Your Site'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {/* Only claim "Live" when we actually verified the script. */}
                              <motion.span
                                className={cn(
                                  "w-1.5 h-1.5 rounded-full",
                                  wasVerified ? "bg-live" : "bg-muted-foreground/40",
                                )}
                                animate={wasVerified ? { scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] } : undefined}
                                transition={{ duration: 2, repeat: Infinity }}
                              />
                              <span className="text-[7px] text-muted-foreground">
                                {wasVerified ? "Live" : "Not detected"}
                              </span>
                            </div>
                          </div>

                          {/* Metrics row */}
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { label: 'Visitors', value: '0', note: 'Waiting...' },
                              { label: 'Page Views', value: '0', note: 'Waiting...' },
                              { label: 'Avg. Duration', value: '--', note: 'No data yet' },
                            ].map((m) => (
                              <div key={m.label} className="p-2 rounded-md bg-muted/40 border border-border/30">
                                <p className="text-[7px] text-muted-foreground">{m.label}</p>
                                <p className="text-sm font-semibold text-foreground tabular-nums mt-0.5">{m.value}</p>
                                <p className="text-[7px] text-muted-foreground/60">{m.note}</p>
                              </div>
                            ))}
                          </div>

                          {/* Empty chart placeholder */}
                          <div className="h-16 rounded-md bg-muted/20 border border-border/30 border-dashed flex items-center justify-center">
                            <div className="text-center">
                              <div className="flex items-center justify-center gap-1 mb-1">
                                <div className="w-6 h-[3px] rounded-full bg-primary/20" />
                                <div className="w-8 h-[3px] rounded-full bg-primary/30" />
                                <div className="w-5 h-[3px] rounded-full bg-primary/15" />
                                <div className="w-10 h-[3px] rounded-full bg-primary/25" />
                                <div className="w-7 h-[3px] rounded-full bg-primary/20" />
                              </div>
                              <p className="text-[7px] text-muted-foreground/50">Data will appear here once tracking is active</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Success text */}
                      <div className="text-center space-y-1">
                        <h2 className="text-lg font-semibold text-foreground">
                          {mode === "addWorkspace"
                            ? `${workspaceName || "Workspace"} Created!`
                            : !wasVerified
                            ? `${siteName || "Your site"} is added`
                            : siteName
                            ? `${siteName} is Ready!`
                            : "You're All Set!"}
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          {mode === "addWorkspace"
                            ? "Your workspace is ready. You can add connectors, team members, and more."
                            : !wasVerified
                            ? "We could not detect the script yet, so tracking is not live. Paste it and publish, and this starts reporting on its own."
                            : mode === "onboarding"
                            ? "Your site has been added. Head to your dashboard to start exploring."
                            : skippedWorkspace
                            ? "Your site has been added. Assign it to a workspace anytime from settings."
                            : "Your site has been added to the workspace."}
                        </p>
                      </div>

                      {/* Primary CTA */}
                      <Button
                        size="lg"
                        className="w-full gap-2"
                        onClick={handleFinishSetup}
                      >
                        {skippedWorkspace ? "Go to Home" : "View Analytics"}
                        <HugeiconsIcon icon={ArrowRight01Icon} size={16} />
                      </Button>

                      {/* Tips */}
                      {mode !== "addWorkspace" && (
                        <div className="rounded-md border border-border/30 bg-muted/20 p-3">
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            <span className="font-medium text-foreground">
                              {wasVerified ? "Pro tip:" : "Next:"}
                            </span>{" "}
                            {wasVerified
                              ? "Visit your website in an incognito window to test that tracking is working. Data appears in real-time."
                              : "Paste the snippet into your site head and publish. You can re-check anytime from Site settings."}
                          </p>
                        </div>
                      )}

                      <p className="text-xs text-muted-foreground text-center">
                        You can add connectors, invite team members, and manage settings from your dashboard.
                      </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </CardContent>
          </Card>

          {/* Footer — help links */}
          <div className="mt-auto pt-5 pb-1">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground/70">Need help setting up?</p>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-[11px] text-muted-foreground hover:text-foreground px-2"
                >
                  <a href="https://docs.humblytics.com" target="_blank" rel="noopener noreferrer">
                    <HugeiconsIcon icon={FileAttachmentIcon} size={12} />
                    Docs
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-[11px] px-2.5"
                  onClick={showIntercom}
                >
                  <HugeiconsIcon icon={Comment02Icon} size={12} />
                  Chat with us
                </Button>
              </div>
            </div>
            <p className="mt-2 text-center text-[11px] text-muted-foreground/50">
              14-day free trial · Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
